import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import * as S from './styles';
import { MdClose } from 'react-icons/md';
export type Variant = 'modal' | 'full' | 'side' | 'block' | 'bottom' | 'none';
type HideFunction = {
  ();
};
export interface Props {
  showCloseButton?: boolean;
  show: boolean;
  setShow?: {
    (show: boolean);
  };
  onHidden?: {
    (hidden: boolean);
  };
  render?: (p: {
    hide: HideFunction;
  }) => React.ReactNode;
  mobileVariant?: Variant;
  desktopVariant?: Variant;
  padding?: string;
  zIndex?: number;
  /** Apenas para full, mostra o modal de baixo para cima */
  transitionReverse?: boolean;
  disableEscapeKeyDown?: boolean;
  sidePosition?: 'left' | 'right';
  dataTestId?: string;
  modalRef?: React.Ref<HTMLDivElement>;
}
import { MODAL_ZINDEX } from '../../util/zIndex';
const Modal: React.FC<React.PropsWithChildren<Props>> = props => {
  const {
    mobileVariant,
    desktopVariant,
    showCloseButton = true,
    render,
    onHidden,
    show,
    setShow,
    zIndex: propZIndex,
    transitionReverse,
    padding = '0',
    disableEscapeKeyDown = false,
    sidePosition = 'right',
    dataTestId,
    modalRef
  } = props;
  const [hidden, setHidden] = useState(() => !show);
  const [appearing, setAppearing] = useState(() => show);
  useLayoutEffect(() => {
    if (show && hidden) {
      setHidden(false);
    } else if (!show && !hidden) {
      const timeoutId = setTimeout(() => {
        setHidden(true);
      }, 300);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [hidden, show]);
  useLayoutEffect(() => {
    if (show) {
      setAppearing(true);
      const timeoutId = setTimeout(() => {
        setAppearing(false);
      }, 400);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [show]);
  const hide: HideFunction = useCallback(() => {
    if (show && setShow) {
      setTimeout(() => {
        setHidden(true);
      }, 300);
      setShow?.(false);
    }
  }, [setShow, show]);
  useEffect(() => {
    onHidden?.(hidden);
  }, [hidden, onHidden]);
  useEffect(() => {
    if (disableEscapeKeyDown) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        hide();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [disableEscapeKeyDown, hide]);
  const zIndex = propZIndex ?? MODAL_ZINDEX;
  if (hidden && !show) return null;
  return <>
      <S.ModalContainer className={`${!show ? 'hidden' : appearing ? 'appearing' : ''} ${desktopVariant === 'side' && sidePosition ? ` side-position-${sidePosition}` : ''}`} mobileVariant={mobileVariant} desktopVariant={desktopVariant} zIndex={zIndex} transitionReverse={transitionReverse} ref={modalRef} data-testid={dataTestId}>
        <S.ModalDialog className={!show ? 'hidden' : appearing ? 'appearing' : ''} mobileVariant={mobileVariant} desktopVariant={desktopVariant} zIndex={zIndex} transitionReverse={transitionReverse}>
          <S.ModalCard className={!show ? 'hidden' : appearing ? 'appearing' : ''} mobileVariant={mobileVariant} desktopVariant={desktopVariant} zIndex={zIndex} transitionReverse={transitionReverse} padding={padding ?? null}>
            {showCloseButton && setShow && <S.ModalCloseButton type="button" onClick={() => hide()} className={!show ? 'hidden' : appearing ? 'appearing' : ''} mobileVariant={mobileVariant} desktopVariant={desktopVariant} zIndex={zIndex} transitionReverse={transitionReverse}>
                <MdClose className="close-icon" />
              </S.ModalCloseButton>}
            <S.ModalContent className={!show ? 'hidden' : appearing ? 'appearing' : ''} mobileVariant={mobileVariant} desktopVariant={desktopVariant} zIndex={zIndex} transitionReverse={transitionReverse} padding={padding ?? null}>
              {props.children}
              {render ? render({
              hide
            }) : null}
            </S.ModalContent>
          </S.ModalCard>
        </S.ModalDialog>
      </S.ModalContainer>
      <S.ModalBackdrop className={!show ? 'hidden' : appearing ? 'appearing' : ''} mobileVariant={mobileVariant} desktopVariant={desktopVariant} zIndex={zIndex} transitionReverse={transitionReverse} />
    </>;
};
export default Modal;